import React from 'react';
import SortTable from '../SortTable';
import ListResults from '../ListResults';
import shallowequal from 'shallowequal';
import Icon from '../Icon';
import {Link, browserHistory} from 'react-router';
import svgEdit from '../../svgs/edit.svg';
import filter from "../../filter";
import {executeBackendQuery} from "../../requests/executeBackendQuery";
import {getData} from "../../requests/CRUD";
import {TEAM} from "../../consts/tables";
import {TEAM_MEMBERS_LIST, TEAM_MEMBERS_LIST_COUNT} from '../../consts/queries';
import LinkComp from "../LinkComp";
import plus from "../../svgs/plus-white.svg";
import print from "../../svgs/print.svg"
import { formattedDate, renderPrintFooter} from "../../utils";
import { getInPoolListDisplay, getOnPhonesListDisplay, getTeamMemberSortValue, getYearsAtSummitDisplay, appliesToPhonesAndPools } from "../../utils/teamMemberDerivedFields";
import Dropdown from '../Dropdown/Dropdown';
import SplitButton from '../SplitButton/SplitButton';
import {buildServerQuery, applySort, parseOrderArray} from "../../sort-utils";
import {can} from "../../access";
import {accessLevelsApi} from "../../requests/accessLevels";
import {hasNoAccessLevel} from "../../access-level-flag";

const limit = 50;

const STATUS_OPTIONS = ['Active', 'Terminated', 'All'];
const DEFAULT_STATUS = 'Active';

// Status query param defaults to Active so the plain /teamMembers landing link
// (no status param) shows the active roster.
const getStatusFromQuery = (query) => {
    const raw = query && query.status;
    if (!raw) return DEFAULT_STATUS;
    const match = STATUS_OPTIONS.find(option => option.toLowerCase() === String(raw).toLowerCase());
    return match || DEFAULT_STATUS;
};

const filterByStatus = (items, status) => {
    if (!Array.isArray(items)) return [];
    if (status === 'Terminated') return items.filter(item => !!item.TERMINATION_DATE);
    if (status === 'All') return items;
    return items.filter(item => !item.TERMINATION_DATE);
};

const TERMINATION_DATE_COLUMN = {
    code: 'TERMINATION_DATE',
    name: 'Termination Date',
    render: (el) => el.TERMINATION_DATE ? formattedDate(el.TERMINATION_DATE) : '',
};

// Only worth showing once the list is actually scoped to terminated members.
// Placed right after the edit column and before Photo, per customer request.
const withTerminationDateColumn = (cols, status) => {
    if (status !== 'Terminated') return cols;
    if (cols.some(col => col.code === 'TERMINATION_DATE')) return cols;
    const photoIndex = cols.findIndex(col => col.code === 'PHOTO');
    const insertIndex = photoIndex === -1 ? cols.length : photoIndex;
    return [
        ...cols.slice(0, insertIndex),
        TERMINATION_DATE_COLUMN,
        ...cols.slice(insertIndex),
    ];
};

// Helper function to get role display name
const getRoleDisplayName = (roleCode) => {
    switch(roleCode) {
        case 'a': // Summit Agent
            return 'Summit - Agent';
        case 'e': // Summit Employee
            return 'Summit - Employee';
        case 'p': // Peak Agent
            return 'Peak - Agent';
        case 'q': // Peak Employee
            return 'Peak - Employee';
        case 'h':
            return 'HSC';
        // Legacy codes
        case 'y':
            return 'Agent';
        case 'n':
            return 'Employee';
        default:
            return 'Unknown';
    }
};

// Helper to check if role should show iCLIMB fields
const shouldShowIClimb = (roleCode) => {
    return roleCode === 'a' || roleCode === 'y' || roleCode === 'h'; // Summit Agent, old Agent, or HSC
};

// On Phones / In Pools only apply to Summit Agents (and legacy agent code).
const shouldShowPhonesAndPools = appliesToPhonesAndPools;

/** DB In Pool first; live Start+270 when empty. List hides dates before current year. */
const renderInPool = (el) => {
    if (!shouldShowPhonesAndPools(el.IS_AGENT)) return '';
    const inPool = getInPoolListDisplay(el);
    return inPool ? (formattedDate(inPool) || '') : '';
};

/** DB On Phones first; live Start+90 when empty. List hides dates before current year. */
const renderOnPhones = (el) => {
    if (!shouldShowPhonesAndPools(el.IS_AGENT)) return '';
    const onPhones = getOnPhonesListDisplay(el);
    return onPhones ? (formattedDate(onPhones) || '') : '';
};

// Only Summit Agents with a current-year date fill these columns — a handful of
// rows out of the whole roster. Leading blanks read as "the sort did nothing",
// so dated rows come first whichever way the column is sorted.
const DATED_COLUMNS = ['ON_PHONES', 'IN_POOL'];

const withDatedRowsFirst = (items, queryOrder) => {
    const primary = parseOrderArray(queryOrder)[0];
    const code = primary ? primary.split('.')[0] : null;
    if (DATED_COLUMNS.indexOf(code) === -1) return items;
    const isDated = (item) => getTeamMemberSortValue(item, code) != null;
    return [...items.filter(isDated), ...items.filter(item => !isDated(item))];
};

const rosterColumns = [
    {
        code: 'IS_AGENT',
        name: 'Role',
        render: (el) => getRoleDisplayName(el.IS_AGENT),
    },
    {
        code: 'FIRST_NAME',
        sortColumn: 'LAST_NAME',
        name: 'Name',
        columnStyle: {
            minWidth: 150
        },
        render: (el) => el.FIRST_NAME+" "+el.LAST_NAME,
    },
    {
        code: 'ADDRESS',
        name: 'Address',
        columnStyle: {
            minWidth: 230
        },
        render: (el) => el.ADDRESS,
    },
    {
        code: 'CITY',
        name: 'City',
        render: (el) => el.CITY,
    },
    {
        code: 'STATE',
        name: 'State',
        render: (el) => el.STATE,
        className: 'paddingLeft'
    },
    {
        code: 'ZIP',
        name: 'ZIP',
        columnStyle: {
            minWidth: 80
        },
        render: (el) => el.ZIP,
    },
    {
        code: 'YRS_AT_STRIVE',
        name: 'Years At Summit',
        render: (el) => getYearsAtSummitDisplay(el),
        className: 'paddingLeft55'
    },
    {
        code: 'STARTING_DATE',
        name: 'Start Date',
        render: (el) => el.STARTING_DATE?formattedDate(el.STARTING_DATE):'',
    },
];


const callListColumns = [
    {
        code: 'IS_AGENT',
        name: 'Role',
        render: (el) => getRoleDisplayName(el.IS_AGENT),
    },
    {
        code: 'FIRST_NAME',
        sortColumn: 'LAST_NAME',
        name: 'Name',
        columnStyle: {
            minWidth: 150
        },
        render: (el) => el.FIRST_NAME+" "+el.LAST_NAME,
    },
    // {
    //     code: 'BIO',
    //     name: 'Bio',
    //     columnStyle: {
    //         minWidth: 220,
    //         maxWidth: 320
    //     },
    //     render: (el) => {
    //         const bio = el.BIO || '';
    //         return bio.length > 140 ? `${bio.slice(0, 140)}…` : bio;
    //     },
    // },
    {
        code: 'PHONE',
        name: 'Work Phone',
        columnStyle: {
            minWidth: 120
        },
        render: (el) => el.PHONE,
    },
    {
        code: 'CELL_PHONE',
        name: 'Cell Phone',
        columnStyle: {
            minWidth: 120
        },
        render: (el) => el.CELL_PHONE,
    },
    // {
    //     code: 'BIRTH_DATE',
    //     name: 'Birth Date',
    //     render: (el) => el.BIRTH_DATE?formattedDate(el.BIRTH_DATE):'',
    // }
];


const listingColumn = [
    {
        code: 'FIRST_NAME',
        sortColumn: 'LAST_NAME',
        name: 'Name',
        columnStyle: {
            minWidth: 150
        },
        render: (el) => el.FIRST_NAME+" "+el.LAST_NAME,
    },
    {
        code: 'ON_PHONES',
        name: 'On Phones',
        render: (el) => renderOnPhones(el),
    },
    {
        code: 'IN_POOL',
        name: 'In Pools',                
        render: (el) => renderInPool(el),
    },
    {
        code: 'FIRST_LISTING',
        name: '1st Listing',
        render: (el) => el.FIRST_LISTING && shouldShowIClimb(el.IS_AGENT) ? formattedDate(el.FIRST_LISTING):'',
    },
    {
        code: 'FIRST_CLOSING',
        name: '1st Closing',
        render: (el) => el.FIRST_CLOSING && shouldShowIClimb(el.IS_AGENT) ? formattedDate(el.FIRST_CLOSING):'',
    }
];

const columns = [
    {
        name: '',
        textAlign: 'center',
        columnStyle: {width: 70},
        render: (el) => (
            <Link to={{pathname: `${arc.path.base}teamMember/${el.ARCIMEDES_ROW_ID}`}}><Icon svg={svgEdit}/></Link>
        ),
    },
            {
                code: 'PHOTO',
                name: 'Photo',
                textAlign: 'center',
                columnStyle: {width: 80},
                render: (el) => {
                    if (!el.PHOTO) return null;
                    // Construct URL: handle both absolute URLs and relative paths
                    let photoUrl;
                    if (el.PHOTO.startsWith('http://') || el.PHOTO.startsWith('https://')) {
                        photoUrl = el.PHOTO;
                    } else {
                        // Ensure proper path construction without double slashes
                        const mediaPath = arc.path.media.endsWith('/') ? arc.path.media.slice(0, -1) : arc.path.media;
                        const photoPath = el.PHOTO.startsWith('/') ? el.PHOTO : '/' + el.PHOTO;
                        photoUrl = mediaPath + photoPath;
                    }
                    return (
                        <img
                            src={photoUrl}
                            alt={`${el.FIRST_NAME || ''} ${el.LAST_NAME || ''}`}
                            style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                            onClick={() => window.open(photoUrl, '_blank')}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    );
                },
            },
    {
        code: 'IS_AGENT',
        name: 'Role',
        render: (el) => getRoleDisplayName(el.IS_AGENT),
    },
    {
        code: 'FIRST_NAME',
        sortColumn: 'LAST_NAME',
        name: 'Name',
        columnStyle: {
            minWidth: 150
        },
        render: (el) => el.FIRST_NAME+" "+el.LAST_NAME,
    },
    {
        code: 'PHONE',
        name: 'Work Phone',
        columnStyle: {
            minWidth: 120
        },
        render: (el) => el.PHONE,
    },

    {
        code: 'CELL_PHONE',
        name: 'Cell Phone',
        columnStyle: {
            minWidth: 120
        },
        render: (el) => el.CELL_PHONE,
    },
    {
        code: 'STARTING_DATE',
        name: 'Start Date',
        render: (el) => el.STARTING_DATE?formattedDate(el.STARTING_DATE):'',
    },
    {
        code: 'YRS_AT_STRIVE',
        name: 'Years At Summit',
        textAlign: 'center',
        render: (el) => getYearsAtSummitDisplay(el),
    },
    {
        code: 'BIRTH_DATE',
        name: 'Birth Date',
        render: (el) => el.BIRTH_DATE?formattedDate(el.BIRTH_DATE):'',
    },
    {
        code: 'ON_PHONES',
        name: 'On Phones',
        render: (el) => renderOnPhones(el),
    },
    {
        code: 'IN_POOL',
        name: 'In Pools',
        render: (el) => renderInPool(el),
    },
    // {
    //     code: 'FIRST_LISTING',
    //     name: '1st Listing',
    //     render: (el) => el.FIRST_LISTING && shouldShowIClimb(el.IS_AGENT) ? formattedDate(el.FIRST_LISTING):'',
    // },
    // {
    //     code: 'FIRST_CLOSING',
    //     name: '1st Closing',
    //     render: (el) => el.FIRST_CLOSING && shouldShowIClimb(el.IS_AGENT) ? formattedDate(el.FIRST_CLOSING):'',
    // },
    {
        code: 'ADDRESS',
        name: 'Address',
        columnStyle: {
            minWidth: 230
        },
        render: (el) => el.ADDRESS,
    },
    {
        code: 'CITY',
        name: 'City',
        render: (el) => el.CITY,
    },
    {
        code: 'STATE',
        name: 'State',
        render: (el) => el.STATE,
        className: 'textCenter'
    },
    {
        code: 'ZIP',
        name: 'ZIP',
        columnStyle: {
            minWidth: 80
        },
        render: (el) => el.ZIP,
    }
];

/**
 * The Name column, flagged for members with no usable access level — they can
 * open almost nothing until a manager gives them one. On screen only: the
 * printed lists keep their plain columns.
 */
const withNoLevelFlag = (cols, levelCodes) => cols.map((col) => (col.name !== 'Name' ? col : {
    ...col,
    render: (el) => (
        <span>
            {col.render(el)}
            {hasNoAccessLevel(el.ACCESS_LEVEL, levelCodes) && (
                <span className="TeamMemberList_noLevel"
                      title="No access level: they can open almost nothing. Set one on their Account Settings tab.">
                    No access level
                </span>
            )}
        </span>
    ),
}));

export default class TeamMemberList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            original: [],
            totalCount: null,
            error: null,
            isBusy: null,
            printType: 'All Fields',
            printColumns: columns,
            width: 0,
            searchQuery: '',
            statusDropdownOpen: false,
            levelCodes: null,
        };
        this.statusDropdownRef = React.createRef();
        this.handleDropdownChange = this.handleDropdownChange.bind(this)
        this.handleSearchChange = this.handleSearchChange.bind(this)
        this.renderPrintPage = this.renderPrintPage.bind(this)
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this)
        this.toggleStatusDropdown = this.toggleStatusDropdown.bind(this)
        this.handleStatusSelect = this.handleStatusSelect.bind(this)
        this.handleDocumentClickForStatusDropdown = this.handleDocumentClickForStatusDropdown.bind(this)
    }

    componentDidMount() {
        this.fetch(this.props.location.query);
        accessLevelsApi.options()
            .then((options) => this.setState({ levelCodes: (options || []).map((o) => o.code) }))
            .catch(() => {});
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
        document.addEventListener('mousedown', this.handleDocumentClickForStatusDropdown);
      }

      componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
        document.removeEventListener('mousedown', this.handleDocumentClickForStatusDropdown);
      }

      updateWindowDimensions() {
        this.setState({ width: window.innerWidth });
      }


    fetch(q) {
        this.setState({isBusy: true});

        const query = {...q};
        const options = filter(buildServerQuery(query), 'team-member');
        options.param.limit = limit;
        options.param.offset = query.page ? ((Number(query.page) - 1) * limit) : 0;

        Promise.all([
            executeBackendQuery({queryName: TEAM_MEMBERS_LIST, options: options}),
            executeBackendQuery({queryName: TEAM_MEMBERS_LIST_COUNT, options: options})
        ]).then(res => {
            const listRes = res && res[0] ? res[0] : {};
            const countRes = res && res[1] ? res[1] : {};
            const listData = Array.isArray(listRes.data) ? listRes.data : [];
            const activeMembersWithoutNullInNames = listData.filter(item => item && item.DELETED !== 'y' && (item.FIRST_NAME !== null && item.LAST_NAME !== null));
            const activeMembers = activeMembersWithoutNullInNames.filter(item => item.DELETED !== '1');
            const sortedMembers = withDatedRowsFirst(
                applySort(activeMembers, query.order, getTeamMemberSortValue),
                query.order,
            );
            this.setState({
                items: sortedMembers,
                original: sortedMembers,
                totalCount: sortedMembers.length,
                error: listRes.success === false ? (listRes.message || 'Failed to load team members') : null,
                isBusy: null,
            });
        }).catch(err => {
            console.error('Error loading team members', err);
            this.setState({
                items: [],
                original: [],
                totalCount: 0,
                error: (err && err.message) ? err.message : 'Failed to load team members',
                isBusy: null,
            });
        }).then(() => {
            // If we still have no items (queries may be missing), fall back to raw table data
            if (this.state.items && this.state.items.length) return;
            getData({
                tableCode: TEAM,
                options: {
                    filter: [
                        // Active records may be stored as n, 0, or null depending on how they were created.
                        { text: 'n', code: 'DELETED', type: 'exact' },
                        { text: '0', code: 'DELETED', type: 'exact' },
                        { text: '', code: 'DELETED', type: 'null' },
                    ],
                    filterType: 'or',
                    order: [
                        { code: 'LAST_NAME', type: 'asc' },
                        { code: 'FIRST_NAME', type: 'asc' },
                    ],
                    limit: { count: limit, offset: options.param.offset || 0 },
                }
            }).then(fallbackRes => {
                const data = Array.isArray(fallbackRes && fallbackRes.data) ? fallbackRes.data : [];
                const active = data.filter(item => item && item.FIRST_NAME && item.LAST_NAME);
                this.setState({
                    items: active,
                    original: active,
                    totalCount: active.length,
                    error: fallbackRes && fallbackRes.success === false ? (fallbackRes.message || 'Failed to load team members') : null,
                    isBusy: null,
                });
            }).catch(err => {
                console.error('Fallback load failed', err);
                this.setState({
                    items: [],
                    original: [],
                    totalCount: 0,
                    error: (err && err.message) ? err.message : 'Failed to load team members',
                    isBusy: null,
                });
            });
        });
    }

    componentDidUpdate(prevProps) {
        if (!shallowequal(prevProps.location.query, this.props.location.query)) {
            this.fetch(this.props.location.query);
        }
    }

    handleDropdownChange(option){
        switch (option) {
            case 'Roster':
                this.setState({
                    printType: 'Roster',
                    printColumns: rosterColumns,
                    items: [...this.state.original],
                    totalCount: this.state.original.length,
                })
                break;
            case 'Call List':
                this.setState({
                    printType: 'Call List',
                    printColumns: callListColumns,
                    items: [...this.state.original],
                    totalCount: this.state.original.length,
                })
                break;
            case '1st Listing & 1st Closing':
                const agentsList = this.state.original.filter(item => item.IS_AGENT === 'y')
                this.setState({
                    printType: '1st Listing & 1st Closing',
                    printColumns: listingColumn,
                    items: agentsList,
                    totalCount: agentsList.length,
                })
                break;
            case 'All Fields':
                this.setState({
                    printType:  'All Fields',
                    printColumns: columns,
                    items: [...this.state.original],
                    totalCount: this.state.original.length,
                })
                break;
        }
    }

    handleSearchChange(e) {
        this.setState({ searchQuery: e.target.value });
    }

    toggleStatusDropdown() {
        this.setState(prevState => ({ statusDropdownOpen: !prevState.statusDropdownOpen }));
    }

    handleDocumentClickForStatusDropdown(e) {
        if (this.statusDropdownRef.current && !this.statusDropdownRef.current.contains(e.target)) {
            this.setState({ statusDropdownOpen: false });
        }
    }

    handleStatusSelect(status) {
        const query = { ...this.props.location.query };
        delete query.page;
        if (status === DEFAULT_STATUS) {
            delete query.status;
        } else {
            query.status = status;
        }
        this.setState({ statusDropdownOpen: false });
        browserHistory.push({ pathname: this.props.location.pathname, query });
    }

    getDisplayItems() {
        const statusFiltered = filterByStatus(this.state.items, getStatusFromQuery(this.props.location.query));
        const q = (this.state.searchQuery || '').trim().toLowerCase();
        if (!q) return statusFiltered;
        return statusFiltered.filter(item => {
            const fullName = ((item.FIRST_NAME || '') + ' ' + (item.LAST_NAME || '')).toLowerCase();
            return fullName.includes(q);
        });
    }

    renderStatusDropdown() {
        const status = getStatusFromQuery(this.props.location.query);
        return (
            <div className='TeamMemberList_status-dropdown' ref={this.statusDropdownRef}>
                <button
                    type='button'
                    className='TeamMemberList_status-dropdown_button'
                    onClick={this.toggleStatusDropdown}
                >
                    <span>{status}</span>
                    <span className='TeamMemberList_status-dropdown_arrow'>{this.state.statusDropdownOpen ? '▲' : '▼'}</span>
                </button>
                {this.state.statusDropdownOpen && (
                    <ul className='TeamMemberList_status-dropdown_list'>
                        {STATUS_OPTIONS.map(option => (
                            <li
                                key={option}
                                className={`TeamMemberList_status-dropdown_item ${option === status ? 'selected' : ''}`}
                                onClick={() => this.handleStatusSelect(option)}
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }

    renderTeamStats() {
        const items = filterByStatus(this.state.original, getStatusFromQuery(this.props.location.query));
        if (!items || !items.length) return null;

        const summitAgents = items.filter(item => item.IS_AGENT === 'a').length;
        const summitEmployees = items.filter(item => item.IS_AGENT === 'e').length;
        const summit = summitAgents + summitEmployees;
        const peak = items.filter(item => item.IS_AGENT === 'p' || item.IS_AGENT === 'q').length;
        const total = summit + peak;

        const stats = [
            { label: 'Total', value: total },
            { label: 'Summit', value: summit },
            { label: 'Summit - Agents', value: summitAgents },
            { label: 'Summit - Employees', value: summitEmployees },
            { label: 'Peak', value: peak },
        ];

        return (
            <div className='TeamMemberList_stats'>
                {stats.map((stat, i) => (
                    <div className='TeamMemberList_stats_item' key={i}>
                        <span className='TeamMemberList_stats_value'>{stat.value}</span>
                        <span className='TeamMemberList_stats_label'>{stat.label}</span>
                    </div>
                ))}
            </div>
        );
    }

    renderPrintPage(displayItems, columns) {
        const splitObjectIntoChunks = (entries, chunkSize = 8) => {
            return Array.from({ length: Math.ceil(entries.length / chunkSize) }, (_, i) =>
             entries.slice(i * chunkSize, (i + 1) * chunkSize)
            );
          };

        // Split rows into chunks
        const rowChunks = splitObjectIntoChunks(displayItems);
        return (
            <React.Fragment>
            {rowChunks.map((rowChunk, chunkIndex) => (
                    <div className='print-container'  key={chunkIndex} style={{background: `url('${arc.path.media+'Transaction_Background.png'}')`}} >
                        {chunkIndex === 0 && <div className='TeamMemberList_head_left'>
                            <h3 className='TeamMemberList_h3'>Team Members - {displayItems.length}</h3>
                        </div>}
                        <div className={`table-print-container ${chunkIndex !== 0 && 'marginTop50'}`}>
                            <SortTable columns={columns || this.state.printColumns}
                                    items={rowChunk}
                                    query={this.props.location.query}
                            />
                        </div>
                        {renderPrintFooter(true)}
                    </div>

             ))}
              </React.Fragment>
        )
    }

    render() {
        const query = this.props.location.query;
        const displayItems = this.getDisplayItems();
        const status = getStatusFromQuery(query);
        const visibleColumns = withTerminationDateColumn(this.state.printColumns, status);

        return (
            <div className='TeamMemberList' data-is-busy={this.state.isBusy}>
                {this.renderPrintPage(displayItems, visibleColumns)}
                    <div className='page-rounded-corner-container'>
                        <div className='page-header-container list'>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                {this.renderTeamStats()}
                            </div>
                            <div className='page-header list'>
                                <div className='page-title'>Team Members</div>
                                {/* Adding a member sets their login — account settings, which Marketing and Partners cannot change. */}
                                {can('team.accounts', 'edit') && <LinkComp to={`${arc.path.base}teamMember`}>
                                    <div className="button-new plus positive">
                                        <Icon svg={plus}/>
                                    </div>
                                </LinkComp>}
                                <input
                                    type="text"
                                    className="TeamMemberList_search"
                                    placeholder="Search by name..."
                                    value={this.state.searchQuery}
                                    onChange={this.handleSearchChange}
                                />
                                <div style={{display: 'flex',justifyContent: 'end', width: this.state.width-466}}>
                                    {this.renderStatusDropdown()}
                                    <SplitButton icon={(this.state.printType==='All Fields' || this.state.printType==='1st Listing & 1st Closing') ? false:true} options={['All Fields', 'Roster', 'Call List', '1st Listing & 1st Closing']}
                                        onChange={this.handleDropdownChange}
                                        selectedOption={this.state.printType}
                                        onClick={()=>(this.state.printType !=='All Fields' && this.state.printType !=='1st Listing & 1st Closing') && window.print()}
                                    />
                                </div>
                            </div>
                            <div className='line'/>
                        </div>
                    </div>
                    <div className='page-body-container'>
                        <SortTable columns={withNoLevelFlag(visibleColumns, this.state.levelCodes)}
                                items={displayItems}
                                query={query}
                                classNameModifier='team-member'
                        />
                        <ListResults query={this.props.location.query}
                                    pathname={this.props.location.pathname}
                                    error={this.state.error}
                                    totalCount={(this.state.searchQuery.trim() || status !== 'All') ? displayItems.length : this.state.totalCount}
                                    itemCount={displayItems.length}
                                    limit={limit}
                        />
                    </div>
            </div>
        );
    }
}
